import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { guardedUpload, withQuery } from "./upload_guard";
import { notify } from "./modal_center";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function fmtSize(n) { n = Number(n||0); if (n < 1024) return `${n} B`; if (n < 1024*1024) return `${(n/1024).toFixed(1)} KB`; return `${(n/1024/1024).toFixed(2)} MB`; }

// Iter 140 — Registry paste clipboard: hanya SATU panel aktif menerima Ctrl+V / Cmd+V.
// Menghindari duplikasi upload ketika beberapa ProductFilesPanel di-mount bersamaan
// (mis. Nota Edit dengan banyak produk). Panel menjadi aktif saat di-klik/di-focus.
// Handler `paste` document tunggal (ref-count) merutekan ke panel yang tercatat aktif.
let __activePastePanel = null; // { id, upload }
let __pasteHandlerRefCount = 0;
let __pasteHandlerRef = null;
function isEditableTarget(t) {
  if (!t) return false;
  const tag = String(t.tagName || "").toUpperCase();
  return tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable === true;
}
function extForType(type) {
  const t = String(type || "").toLowerCase();
  if (t === "image/jpeg") return "jpg";
  if (t === "image/png") return "png";
  if (t === "image/webp") return "webp";
  return "png";
}
function pasteFilenameFor(type) {
  const stamp = new Date().toISOString().replace(/[-:T.]/g,"").slice(0,14);
  return `paste-${stamp}.${extForType(type)}`;
}
function attachPasteHandler() {
  __pasteHandlerRefCount += 1;
  if (__pasteHandlerRef) return;
  __pasteHandlerRef = (e) => {
    // Biarkan paste teks pada input/textarea/contenteditable berjalan normal (mis. kolom catatan).
    if (isEditableTarget(e.target)) return;
    if (!__activePastePanel) return;
    const items = e.clipboardData && e.clipboardData.items ? e.clipboardData.items : [];
    for (const it of items) {
      if (it.kind === "file" && String(it.type || "").startsWith("image/")) {
        const blob = it.getAsFile();
        if (!blob) continue;
        e.preventDefault();
        const named = new File([blob], pasteFilenameFor(blob.type), { type: blob.type });
        try { __activePastePanel.upload(named, { source: "paste" }); } catch (_) {}
        return;
      }
    }
  };
  document.addEventListener("paste", __pasteHandlerRef);
}
function detachPasteHandler() {
  __pasteHandlerRefCount = Math.max(0, __pasteHandlerRefCount - 1);
  if (__pasteHandlerRefCount === 0 && __pasteHandlerRef) {
    document.removeEventListener("paste", __pasteHandlerRef);
    __pasteHandlerRef = null;
  }
}
function markActivePanel(id, upload) {
  __activePastePanel = { id, upload };
}
function clearActivePanel(id) {
  if (__activePastePanel && __activePastePanel.id === id) __activePastePanel = null;
}

export function ProductFilesPanel({ orderNumber, productId, compact }) {
  const [state, setState] = useState({ files: [], caps: null, actor: null, stage: "", loading: true, error: "" });
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState("");
  const [previewType, setPreviewType] = useState("");
  const [pasteActive, setPasteActive] = useState(false);
  const fileInputRef = useRef(null);
  const wrapperRef = useRef(null);
  const load = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: "" }));
    try {
      const r = await axios.get(`${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(productId)}/files`, { withCredentials:true });
      setState({ files: r.data.files || [], caps: r.data.capabilities || {}, actor: r.data.actor || null, stage: r.data.stage || "", loading: false, error: "" });
    } catch (e) {
      const st = e.response?.status;
      const msg = st === 403 ? "Anda tidak berwenang mengakses file produk ini." : (e.response?.data?.detail || "Gagal memuat file.");
      setState({ files: [], caps: null, actor: null, stage: "", loading: false, error: msg });
    }
  }, [orderNumber, productId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  const onPickFile = () => fileInputRef.current?.click();
  // Iter 140 — Alur upload yang dipakai oleh file picker & clipboard paste.
  //   Validasi ukuran + tipe SAMA dengan sebelumnya (10 MB, JPG/JPEG/PNG/WebP/PDF).
  //   `opts.source` hanya untuk pesan status (mis. "paste" vs "pick").
  const uploadFile = useCallback(async (f, opts = {}) => {
    if (!f) return;
    if (f.size > 10*1024*1024) { notify.warning({ title:"Ukuran file terlalu besar", message:"Ukuran maksimum adalah 10 MB. Silakan pilih file yang lebih kecil." }); return; }
    const okTypes = ["image/jpeg","image/png","image/webp","application/pdf"];
    if (!okTypes.includes(f.type) && !/\.(jpg|jpeg|png|webp|pdf)$/i.test(f.name)) { notify.warning({ title:"Format file tidak didukung", message:"Gunakan format JPG, JPEG, PNG, WebP, atau PDF." }); return; }
    try {
      const fd = new FormData(); fd.append("file", f);
      const url = `${API}/orders/${encodeURIComponent(orderNumber)}/products/${encodeURIComponent(productId)}/files`;
      const res = await guardedUpload({ precheckUrl: `${url}/precheck`, file: f, send: (g) => {
        setBusy(true);
        return axios.post(withQuery(url, g), fd, { withCredentials:true, headers:{"Content-Type":"multipart/form-data"} }).finally(() => setBusy(false));
      } });
      if (!res) return;
      if (opts.source === "paste") notify.success({ title:"Tempel berhasil", message:`Gambar dari clipboard tersimpan (${f.name}).` });
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const status = err.response?.status;
      const msg = status === 403 ? "Anda tidak berwenang mengunggah file di tahap ini." : (detail || "Silakan coba lagi.");
      notify.error({ title:"Upload gagal", message: msg });
    }
    finally { setBusy(false); }
  }, [orderNumber, productId, load]);
  const onFileChange = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    await uploadFile(f, { source: "pick" });
  };
  // Iter 140 — Aktivasi panel utk paste (Ctrl+V / Cmd+V). Panel aktif menerima paste
  //   selama masih ter-mount. Klik/focus di panel lain akan memindahkan aktivasi.
  const canPaste = !!(state.caps || {}).can_upload;
  useEffect(() => {
    if (!canPaste) return;
    attachPasteHandler();
    return () => { clearActivePanel(productId); detachPasteHandler(); };
  }, [canPaste, productId]);
  const activate = useCallback(() => {
    if (!canPaste) return;
    markActivePanel(productId, uploadFile);
    setPasteActive(true);
  }, [canPaste, productId, uploadFile]);
  const deactivate = useCallback(() => {
    // Delay agar klik pada tombol di dalam panel tidak langsung men-deaktivasi.
    setTimeout(() => {
      if (!wrapperRef.current) return;
      if (document.activeElement && wrapperRef.current.contains(document.activeElement)) return;
      // Hanya clear bila panel ini yang aktif — jangan menghapus aktivasi panel lain.
      if (__activePastePanel && __activePastePanel.id === productId) {
        clearActivePanel(productId);
        setPasteActive(false);
      }
    }, 0);
  }, [productId]);
  // Iter 140 — Tombol "Tempel Gambar" via Clipboard API (fallback bila Ctrl+V tidak ke-track).
  const onClickPaste = useCallback(async () => {
    if (!canPaste || busy) return;
    activate();
    if (!(navigator.clipboard && navigator.clipboard.read)) {
      notify.info ? notify.info({ title:"Siap menerima paste", message:"Tekan Ctrl+V (atau Cmd+V di Mac) untuk menempelkan gambar dari clipboard." })
                 : notify.warning({ title:"Siap menerima paste", message:"Tekan Ctrl+V (atau Cmd+V di Mac) untuk menempelkan gambar dari clipboard." });
      return;
    }
    try {
      const items = await navigator.clipboard.read();
      for (const it of items) {
        for (const type of it.types) {
          if (String(type).startsWith("image/")) {
            const blob = await it.getType(type);
            const named = new File([blob], pasteFilenameFor(type), { type });
            await uploadFile(named, { source: "paste" });
            return;
          }
        }
      }
      notify.warning({ title:"Clipboard kosong", message:"Tidak ada gambar di clipboard. Salin screenshot atau gambar lebih dulu, atau tekan Ctrl+V/Cmd+V." });
    } catch (err) {
      // Umumnya karena izin clipboard belum diberikan → arahkan pakai Ctrl+V.
      notify.warning({ title:"Siap menerima paste", message:"Tekan Ctrl+V (atau Cmd+V di Mac) untuk menempelkan gambar dari clipboard." });
    }
  }, [canPaste, busy, activate, uploadFile]);
  const doPreview = async (f) => {
    try {
      const r = await axios.get(`${API}/files/${encodeURIComponent(f.id)}/preview${f.preview_query || ""}`, { withCredentials:true, responseType:"blob" });
      const url = URL.createObjectURL(r.data);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(url); setPreviewName(f.filename); setPreviewType(f.content_type || "");
    } catch (e) { notify.error({ title:"Gagal memuat pratinjau", message: e.response?.data?.detail || "Silakan coba lagi." }); }
  };
  const closePreview = () => { if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(null); setPreviewType(""); };
  const doDownload = async (f) => {
    try {
      const r = await axios.get(`${API}/files/${encodeURIComponent(f.id)}/download${f.preview_query || ""}`, { withCredentials:true, responseType:"blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename || "download"; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      // Iter 128 — refresh daftar file agar badge Gudang download langsung update.
      load();
    } catch (e) { notify.error({ title:"Gagal mengunduh file", message: e.response?.data?.detail || "Silakan coba lagi." }); }
  };
  const doDelete = async (f) => {
    const isAdmin = state.caps?.can_delete_admin;
    let reason = "";
    if (isAdmin) {
      const val = await notify.prompt({
        title: "Hapus file produk?",
        message: `File "${f.filename}" akan dihapus. Wajib isi alasan penghapusan (masuk audit log, min. 3 karakter).`,
        placeholder: "Alasan penghapusan…",
        multiline: true,
        required: true,
        minLength: 3,
        primaryLabel: "Ya, Hapus File",
        secondaryLabel: "Batal",
      });
      if (val === null || val === undefined) return;
      reason = val;
    } else {
      const ok = await notify.confirm({
        title: "Hapus file milik Anda?",
        message: `File "${f.filename}" akan dihapus dari produk ini. Tindakan ini tidak dapat dibatalkan.`,
        primaryLabel: "Ya, Hapus File",
        secondaryLabel: "Batal",
        danger: true,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const url = `${API}/files/${encodeURIComponent(f.id)}${reason ? `?reason=${encodeURIComponent(reason)}` : ""}`;
      await axios.delete(url, { withCredentials:true });
      await load();
    } catch (e) { notify.error({ title:"Gagal menghapus file", message: e.response?.data?.detail || "Silakan coba lagi." }); }
    finally { setBusy(false); }
  };
  const caps = state.caps || {};
  return <div ref={wrapperRef} className={`pf-panel ${compact?"pf-compact":""} ${pasteActive?"pf-paste-active":""}`} data-testid={`pf-panel-${productId}`}
    tabIndex={canPaste?0:-1}
    onMouseDown={canPaste?activate:undefined}
    onFocus={canPaste?activate:undefined}
    onBlur={canPaste?deactivate:undefined}
  >
    <div className="pf-header">
      <div className="pf-title-wrap">
        <strong className="pf-title">File Produk</strong>
        {state.stage && <span className="muted small">Tahap: {state.stage}</span>}
      </div>
      {caps.can_upload && <>
        <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" style={{display:"none"}} onChange={onFileChange} data-testid={`pf-file-input-${productId}`}/>
        <button type="button" data-testid={`pf-paste-btn-${productId}`} className="soft-btn small-btn" disabled={busy} onClick={onClickPaste} title="Tempel gambar dari clipboard (Ctrl+V / Cmd+V)">{busy?"Mengunggah…":"Tempel Gambar"}</button>
        <button type="button" data-testid={`pf-upload-btn-${productId}`} className="soft-btn small-btn" disabled={busy} onClick={onPickFile}>{busy?"Mengunggah…":"+ Upload"}</button>
      </>}
    </div>
    {canPaste && pasteActive && (
      <div className="pf-paste-hint" data-testid={`pf-paste-hint-${productId}`}>
        Panel aktif — tekan <kbd>Ctrl</kbd>+<kbd>V</kbd> (Mac: <kbd>⌘</kbd>+<kbd>V</kbd>) untuk menempel screenshot / gambar dari clipboard.
      </div>
    )}
    {state.loading && <div className="empty small" data-testid={`pf-loading-${productId}`}>Memuat file…</div>}
    {state.error && <div className="pf-error" data-testid={`pf-error-${productId}`}>{state.error}</div>}
    {!state.loading && !state.error && state.files.length === 0 && <div className="empty small" data-testid={`pf-empty-${productId}`}>Belum ada file untuk produk ini.</div>}
    <div className="pf-list">
      {state.files.map(f => {
        const isImg = String(f.content_type||"").startsWith("image/");
        const isPdf = f.content_type === "application/pdf";
        const canDelOwn = state.actor?.kind === "operator" && f.uploader_kind === "operator" && f.uploader_id === state.actor?.id && f.stage === state.stage;
        const showDelete = !f.is_note_primary && (caps.can_delete_admin || canDelOwn);
        return <div key={f.id} className="pf-item" data-testid={`pf-item-${f.id}`}>
          <button type="button" className="pf-thumb-btn" onClick={()=>doPreview(f)} aria-label={`Buka preview ${f.filename}`} data-testid={`pf-thumb-${f.id}`}>
            {isImg
              ? <img className="pf-thumb" src={`${API}/files/${encodeURIComponent(f.id)}/preview${f.preview_query || ""}`} alt="" loading="lazy" draggable="false"/>
              : <span className={`pf-thumb pf-thumb-icon ${isPdf?"is-pdf":""}`} aria-hidden="true">{isPdf?"PDF":"FIL"}</span>
            }
          </button>
          <div className="pf-item-info">
            <strong>{f.filename}</strong>
            {f.is_note_primary && <span className="muted small" data-testid={`pf-follows-primary-${productId}`}>Mengikuti mockup utama (produk ini tidak punya file sendiri)</span>}
            <span className="muted small">{fmtSize(f.size)} · {new Date(f.created_at).toLocaleString("id-ID",{timeZone:"Asia/Jakarta"})} · {f.uploader_name}</span>
            {/* Iter 128 — Badge unduhan Gudang (per file, backend-persisted) */}
            {/* Iter 129 — Deteksi revisi baru: file count=0 + ada file lebih lama yg sudah diunduh Gudang */}
            <GudangDownloadBadge file={f} allFiles={state.files}/>
          </div>
          <div className="pf-item-actions">
            <button type="button" data-testid={`pf-preview-${f.id}`} className="text-btn small-btn" onClick={()=>doPreview(f)}>Pratinjau</button>
            {caps.can_download && <button type="button" data-testid={`pf-download-${f.id}`} className="text-btn small-btn" onClick={()=>doDownload(f)}>Unduh</button>}
            {showDelete && <button type="button" data-testid={`pf-delete-${f.id}`} className="text-btn small-btn danger" onClick={()=>doDelete(f)} disabled={busy}>Hapus</button>}
          </div>
        </div>;
      })}
    </div>
    {previewUrl && <div className="pf-preview-modal" role="dialog" aria-modal="true" data-testid="pf-preview-modal" onClick={closePreview}>
      <div className="pf-preview-inner" onClick={e=>e.stopPropagation()}>
        <div className="pf-preview-head">
          <strong className="pf-preview-name" title={previewName}>{previewName}</strong>
          <button type="button" className="soft-btn small-btn pf-preview-close" data-testid="pf-preview-close" onClick={closePreview} aria-label="Tutup pratinjau">Tutup</button>
        </div>
        <div className="pf-preview-body">
          {previewType === "application/pdf"
            ? <iframe src={previewUrl} title={previewName} className="pf-preview-frame"/>
            : <img src={previewUrl} alt={previewName} className="pf-preview-img" draggable="false"/>
          }
        </div>
      </div>
    </div>}
  </div>;
}

// Iter 128 — Badge status unduhan Gudang. Sumber data: backend (per file, per versi).
// - count 0  → abu-abu "Belum diunduh"
// - count >0 → hijau "✓ Diunduh Gudang N×" + nama + waktu terakhir (WIB)
// Iter 129 — Tambahan state "revisi baru belum diunduh":
// - count 0 + ada file LEBIH LAMA (same product) dgn gudang_download_count>0
//   → merah "⚠ Revisi baru — perlu diunduh ulang". Menghindari Rido melewatkan file revisi.
function GudangDownloadBadge({ file, allFiles }) {
  const count = Number(file?.gudang_download_count || 0);
  const by = file?.gudang_last_downloaded_by || "";
  const at = file?.gudang_last_downloaded_at || "";
  const needsRedownload = count === 0 && Array.isArray(allFiles) && (() => {
    const meAt = new Date(file?.created_at || 0).getTime();
    return allFiles.some(o => {
      if (!o || o.id === file.id) return false;
      const oCount = Number(o.gudang_download_count || 0);
      if (oCount <= 0) return false;
      const oAt = new Date(o.created_at || 0).getTime();
      return oAt < meAt; // ada file lebih lama yg sudah pernah diunduh Gudang
    });
  })();
  if (needsRedownload) {
    return <span className="pf-dl-badge pf-dl-badge-revisi" data-testid={`pf-dl-badge-${file.id}`}
      title="Revisi baru terdeteksi — Gudang sudah unduh versi sebelumnya tapi belum unduh versi ini">
      <span className="pf-dl-badge-warn" aria-hidden="true">!</span>
      Revisi baru — <strong>perlu diunduh ulang</strong>
    </span>;
  }
  if (!count) {
    return <span className="pf-dl-badge pf-dl-badge-none" data-testid={`pf-dl-badge-${file.id}`} title="Belum ada unduhan dari tim Gudang">
      Belum diunduh
    </span>;
  }
  let atLabel = "";
  try { atLabel = new Date(at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { atLabel = at; }
  return <span className="pf-dl-badge pf-dl-badge-done" data-testid={`pf-dl-badge-${file.id}`}
    title={`Terakhir diunduh oleh ${by || "—"} pada ${atLabel || "—"} WIB`}
    data-count={count}>
    <span className="pf-dl-badge-check" aria-hidden="true">✓</span>
    Diunduh Gudang <strong data-testid={`pf-dl-count-${file.id}`}>{count}×</strong>
    {(by || atLabel) && <span className="pf-dl-badge-meta"> · {by || "—"} · {atLabel || "—"}</span>}
  </span>;
}
